/**
 * Aktionen (MP-D): Rabatt für einen Zeitraum, Bundle („ab 3 Teilen −20 %“) oder automatische Preisstaffel nach Liegezeit.
 * Die Kasse rechnet die Aktion beim Scannen von selbst – Etiketten bleiben, wie sie sind.
 */
import { useTx } from "../i18n";
import { useState } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { tagPlus } from "@shared/perioden";
import { STAFFEL_STANDARD, type AktionArt, type StaffelStufe } from "@shared/schemas/aktionen";
import { useAktionAnlegen, useAktionBeenden, useAktionen, type AktionenDaten } from "./aktionen-api";

const ART_TEXT: Record<AktionArt, { titel: string; text: string }> = {
  rabatt: { titel: "Rabatt", text: "Ein Prozentsatz auf alles oder eine Kategorie, für einen Zeitraum." },
  bundle: { titel: "Bundle", text: "Wer mehrere deiner Teile auf einmal kauft, bekommt Prozent auf alle." },
  staffel: { titel: "Preisstaffel", text: "Je länger ein Artikel liegt, desto günstiger – automatisch, ohne neue Etiketten." },
};

export function Aktionen() {
  const q = useAktionen();
  return <Seitenzustand query={q} lines={5}>{(d) => <AktionenSeite d={d} />}</Seitenzustand>;
}

function AktionenSeite({ d }: { d: AktionenDaten }) {
  const tx = useTx();
  const anlegen = useAktionAnlegen();
  const beenden = useAktionBeenden();
  const heute = tagVon();
  const [offen, setOffen] = useState(false);
  const [art, setArt] = useState<AktionArt>("rabatt");
  const [titel, setTitel] = useState("");
  const [prozent, setProzent] = useState(20);
  const [kategorie, setKategorie] = useState("");
  const [von, setVon] = useState(heute);
  const [bis, setBis] = useState<string>(tagPlus(heute, 6));
  const [unbegrenzt, setUnbegrenzt] = useState(false);
  const [bundleAnzahl, setBundleAnzahl] = useState(3);
  const [staffel, setStaffel] = useState<StaffelStufe[]>(STAFFEL_STANDARD);
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const laufend = d.aktionen.filter((a) => a.status !== "beendet");
  const beendet = d.aktionen.filter((a) => a.status === "beendet");

  function standardTitel(a: AktionArt, p: number, k: string, n: number): string {
    if (a === "rabatt") return k ? `${p} % auf ${k}` : `${p} % auf alles`;
    if (a === "bundle") return `Ab ${n} Teilen ${p} % günstiger`;
    return "Je länger, desto günstiger";
  }

  async function absenden() {
    setMeldung(null);
    try {
      await anlegen.mutateAsync({
        art,
        titel: titel.trim() || standardTitel(art, prozent, kategorie, bundleAnzahl),
        prozent,
        kategorie,
        von,
        bis: art === "staffel" || unbegrenzt ? null : bis,
        bundleAnzahl,
        staffel,
      });
      setOffen(false);
      setTitel("");
      setMeldung({ tone: "success", text: "Aktion angelegt – die Kasse rechnet sie ab dem Starttag automatisch." });
    } catch (e) {
      setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Anlegen hat nicht geklappt." });
    }
  }

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-row">
        <Link to="/mieter" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
        <h1 className="title">{tx("Aktionen")}</h1>
      </div>
      <p className="sc-muted">{tx("Die Kasse zieht den Rabatt beim Scannen von selbst ab. Deine Etiketten bleiben, wie sie sind.")}</p>
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      {!d.erlaubt ? <Notice tone="info" title={tx("Eigene Aktionen sind hier nicht freigeschaltet")}>Der Laden hat eigene Aktionen ausgeschaltet. Sprich ihn an, wenn du eine Idee hast.</Notice> : null}

      {d.erlaubt && !offen ? <Button variant="primary" block icon="percent" onClick={() => setOffen(true)}>Aktion anlegen</Button> : null}

      {offen ? (
        <Card title={tx("Neue Aktion")} actions={<Button variant="quiet" icon="x" onClick={() => setOffen(false)}>Abbrechen</Button>}>
          <div className="sc-col" style={{ gap: "var(--space-3)" }}>
            <div role="radiogroup" aria-label="Art der Aktion" className="sc-col" style={{ gap: 6 }}>
              {(Object.keys(ART_TEXT) as AktionArt[]).map((a) => (
                <button key={a} type="button" role="radio" aria-checked={art === a} className={`mp-wahl${art === a ? " is-on" : ""}`} onClick={() => setArt(a)}>
                  <span className="sc-strong">{ART_TEXT[a].titel}</span>
                  <span className="sc-muted sc-small">{ART_TEXT[a].text}</span>
                </button>
              ))}
            </div>
            <FormGrid>
              {art !== "staffel" ? (
                <TextField label={`Prozent (höchstens ${d.maxProzent})`} type="number" inputMode="numeric" min={1} max={d.maxProzent} value={prozent} onChange={(e) => setProzent(Math.max(1, Math.min(d.maxProzent, Number(e.target.value) || 0)))} suffix="%" />
              ) : null}
              {art === "bundle" ? <TextField label="Ab wie vielen Teilen" type="number" inputMode="numeric" min={2} max={20} value={bundleAnzahl} onChange={(e) => setBundleAnzahl(Math.max(2, Math.min(20, Number(e.target.value) || 2)))} /> : null}
              <Select label="Gilt für" value={kategorie} onChange={(e) => setKategorie(e.target.value)}>
                <option value="">Alle meine Artikel</option>
                {d.kategorien.map((k) => <option key={k} value={k}>{k}</option>)}
              </Select>
              <TextField label="Ab" type="date" value={von} min={heute} onChange={(e) => setVon(e.target.value)} />
              {art !== "staffel" && !unbegrenzt ? <TextField label="Bis" type="date" value={bis} min={von} onChange={(e) => setBis(e.target.value)} /> : null}
            </FormGrid>
            {art !== "staffel" ? <label className="sc-check"><input type="checkbox" checked={unbegrenzt} onChange={(e) => setUnbegrenzt(e.target.checked)} /> Läuft, bis ich sie beende</label> : null}
            {art === "staffel" ? (
              <div className="sc-col" style={{ gap: 6 }}>
                <span className="sc-strong">Stufen</span>
                {staffel.map((s, i) => (
                  <div key={i} className="sc-row" style={{ gap: "var(--space-2)" }}>
                    <TextField label={i === 0 ? "Nach Tagen" : ""} aria-label="Nach Tagen" type="number" inputMode="numeric" min={1} max={365} value={s.tage} onChange={(e) => setStaffel(staffel.map((x, j) => (j === i ? { ...x, tage: Number(e.target.value) || 1 } : x)))} />
                    <TextField label={i === 0 ? "Prozent" : ""} aria-label="Prozent" type="number" inputMode="numeric" min={1} max={d.maxProzent} value={s.prozent} onChange={(e) => setStaffel(staffel.map((x, j) => (j === i ? { ...x, prozent: Math.min(d.maxProzent, Number(e.target.value) || 1) } : x)))} suffix="%" />
                    {staffel.length > 1 ? <Button variant="quiet" icon="x" aria-label="Stufe entfernen" onClick={() => setStaffel(staffel.filter((_, j) => j !== i))} /> : null}
                  </div>
                ))}
                {staffel.length < 4 ? <Button variant="quiet" icon="plus" onClick={() => setStaffel([...staffel, { tage: (staffel.at(-1)?.tage ?? 30) + 30, prozent: Math.min(d.maxProzent, (staffel.at(-1)?.prozent ?? 10) + 15) }])}>Stufe hinzufügen</Button> : null}
                <p className="sc-muted sc-small">Gezählt wird ab dem Tag, an dem du den Artikel als eingeräumt gemeldet hast, sonst ab dem Erfassen.</p>
              </div>
            ) : null}
            <TextField label="Titel (optional)" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder={standardTitel(art, prozent, kategorie, bundleAnzahl)} maxLength={60} />
            <Button variant="primary" block icon="check" loading={anlegen.isPending} onClick={() => void absenden()}>Aktion starten</Button>
          </div>
        </Card>
      ) : null}

      {laufend.length ? (
        <>
          <h2 className="heading">{tx("Läuft und geplant")}</h2>
          {laufend.map((a) => (
            <Card key={a.id}>
              <div className="sc-spread" style={{ alignItems: "flex-start" }}>
                <div className="sc-col" style={{ gap: 2, minWidth: 0 }}>
                  <span className="sc-strong">{a.titel}</span>
                  <span className="sc-muted sc-small">{a.text}{a.kategorie && a.art !== "rabatt" ? ` · ${a.kategorie}` : ""}</span>
                  <span className="sc-muted sc-small">{a.status === "geplant" ? `Ab ${formatTag(a.von)}` : a.bis ? `Bis ${formatTag(a.bis)}` : "Bis du sie beendest"}</span>
                </div>
                <StatusBadge status={a.status === "laeuft" ? "paid" : "neutral"}>{a.status === "laeuft" ? "Läuft" : "Geplant"}</StatusBadge>
              </div>
              <div className="sc-spread" style={{ marginTop: "var(--space-3)" }}>
                <span className="sc-small">{a.verkauft ? `${a.verkauft} verkauft · ${formatEuro(a.umsatzCent)} · Rabatt ${formatEuro(a.rabattCent)}` : "Noch nichts darüber verkauft"}</span>
                <Button variant="quiet" icon="x" loading={beenden.isPending && beenden.variables === a.id} onClick={() => confirm("Aktion beenden? Das lässt sich nicht rückgängig machen.") && beenden.mutate(a.id)}>Beenden</Button>
              </div>
            </Card>
          ))}
        </>
      ) : !offen ? (
        <EmptyState illustration="empty-shelf" title={tx("Noch keine Aktion")}>Ein Wochenend-Rabatt oder „ab 3 Teilen 20 %“ bringt Bewegung ins Regal.</EmptyState>
      ) : null}

      {beendet.length ? (
        <details>
          <summary className="heading" style={{ cursor: "pointer" }}>Beendet ({beendet.length})</summary>
          {beendet.map((a) => (
            <p key={a.id} className="sc-spread sc-small" style={{ margin: "6px 0" }}>
              <span>{a.titel} <span className="sc-muted">· {formatTag(a.von)}{a.bis ? ` – ${formatTag(a.bis)}` : ""}</span></span>
              <span className="sc-muted">{a.verkauft} verkauft</span>
            </p>
          ))}
        </details>
      ) : null}
      <p className="sc-muted sc-small"><Icon name="info" size={14} /> Rabatte des Personals an der Kasse (Mängel, Kulanz) haben Vorrang. Die Provision bleibt wie im Vertrag.</p>
    </div>
  );
}
